import { CheckIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { Drawer } from '../shared/Drawer';
import { IntervalChoice } from '../shared/IntervalChoice';
import { dayShort } from '../../core/format/dates';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { policyCopy } from '../../data/policies';
import { euro } from '../../core/format/currency';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import { useSubscriptionBuilder, type BuilderTab } from './useSubscriptionBuilder';
const tabs: {
  key: BuilderTab;
  label: string;
}[] = [{
  key: 'protocol',
  label: 'Een protocol'
}, {
  key: 'products',
  label: 'Losse producten'
}];
const stepTitle = 'text-[13px] text-muted';
const chip = (on: boolean) => `whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] transition-[background-color,color,box-shadow] duration-150 ease-soft ${on ? 'bg-ink text-shell' : 'text-ink ring-1 ring-line hover:ring-ink/40'}`;

/**
 * "Stel je abonnement samen": een lade met drie stappen in één scherm. Wat je bijvult, hoe vaak,
 * en hoe de eerste leveringen eruitzien. De knop legt alles als bijvullen in de mand, met het
 * gekozen ritme; afrekenen gaat daarna gewoon via de mand.
 */
export function SubscriptionBuilder() {
  const b = useSubscriptionBuilder();
  const {
    routineTotals,
    productById
  } = useCatalog();
  const nameOf = (id: string) => productById(id)?.name ?? id;
  const footer = <div data-id="element-8931" data-mp="c2pk6bqlicf0">
      {(b.empty || b.tooSmall) && <p id="builder-empty" className="mb-3 text-center text-[13px] text-muted" data-id="element-8932" data-mp="g75nq6cql7r2">
          {b.empty ? 'Kies eerst wat je wilt bijvullen.' : `Een doos is minstens ${euro(b.minBoxValue)}: voeg nog iets toe.`}
        </p>}
      <button type="button" onClick={b.submit} disabled={b.empty || b.tooSmall} aria-describedby={b.empty || b.tooSmall ? 'builder-empty' : undefined} className={btnPrice} data-id="element-8933" data-mp="px8fy4c1ahoc">
        <span className={btnPriceLabel} data-id="element-8934" data-mp="3tinzicc21dym">In de mand</span>
        <span className={btnPriceAmount} data-id="element-8935" data-mp="10p8ts3aqavvs">
          {euro(b.firstOrder)}
          <span className="text-[12px] opacity-80" data-id="element-8936" data-mp="vs8p9wtj216s"> nu</span>
        </span>
      </button>
      <p className="mt-3 text-center text-[13px] text-muted" data-id="element-8937" data-mp="19jdh4cqnlp6k">Zelfde prijs als los · {policyCopy.subscriptionPerks}</p>
    </div>;
  return <Drawer open={b.open} onClose={b.close} title="Stel je abonnement samen" footer={footer} data-id="element-8938" data-mp="15hs852h8pv1n">
      <section aria-labelledby="builder-what" data-id="element-8939" data-mp="hs7osih6cvru">
        <h3 id="builder-what" className={stepTitle} data-id="element-8940" data-mp="csur12orm4ah">
          1 · Wat vul ik bij
        </h3>
        <div role="tablist" aria-label="Wat vul ik bij" className="mt-3 flex gap-1.5" data-id="element-8941" data-mp="qsclnyoqsbrr">
          {tabs.map(t => <button key={t.key} type="button" role="tab" id={`builder-tab-${t.key}`} aria-selected={b.tab === t.key} aria-controls={`builder-panel-${t.key}`} onClick={() => b.setTab(t.key)} className={chip(b.tab === t.key)} data-id="element-8942" data-mp="lsrkriq0htce">
              {t.label}
            </button>)}
        </div>

        {b.tab === 'protocol' ? <ul id="builder-panel-protocol" role="tabpanel" aria-labelledby="builder-tab-protocol" className="mt-4 divide-y divide-line border-y border-line" data-id="element-8943" data-mp="29oxrviulbsth">
            {b.routineOptions.map(r => {
          const {
            items,
            price,
            months
          } = routineTotals(r);
          const on = b.routineId === r.id;
          return <li key={r.id} data-id="element-8944" data-mp="2tfj9er481bg2">
                  <button type="button" role="checkbox" aria-checked={on} onClick={() => b.toggleRoutine(r.id)} className="flex w-full items-center gap-3 py-3 text-left" data-id="element-8945" data-mp="2tdq35cxist8l">
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${on ? 'bg-ink text-shell' : 'ring-1 ring-line'}`} aria-hidden="true" data-id="element-8946" data-mp="37dranb8aiyx1">
                      {on && <CheckIcon className="h-3 w-3" strokeWidth={2.5} data-id="element-8947" data-mp="1njyarbutmll6" />}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-8948" data-mp="1jhuwygkoj4wq">
                      <span className="block text-[15px] leading-snug text-ink" data-id="element-8949" data-mp="1jk7dqqepb987">{r.name}</span>
                      <span className="block text-[12px] text-muted" data-id="element-8950" data-mp="1el46czb90qx2">
                        {items.length} producten · gaat {months} mee
                      </span>
                    </span>
                    <span className="shrink-0 text-[14px] tabular-nums text-ink" data-id="element-8951" data-mp="276w1hsqe5vwq">{euro(price)}</span>
                  </button>
                </li>;
        })}
          </ul> : <div id="builder-panel-products" role="tabpanel" aria-labelledby="builder-tab-products" className="mt-4" data-id="element-8952" data-mp="1xfsygldwj6ut">
            {b.chips.length > 1 && <div className="-mx-6 flex gap-1.5 overflow-x-auto px-6 pb-1" role="group" aria-label="Filter op huid" data-id="element-8953" data-mp="2pyghlv9c8bt9">
                <button type="button" aria-pressed={b.skin === null} onClick={() => b.setSkin(null)} className={chip(b.skin === null)} data-id="element-8954" data-mp="2l6onxbuvxpyw">
                  Alles
                </button>
                {b.chips.map(s => <button key={s} type="button" aria-pressed={b.skin === s} onClick={() => b.setSkin(b.skin === s ? null : s)} className={chip(b.skin === s)} data-id="element-8955" data-mp="2xjscpwbyrhh8">
                    {s}
                  </button>)}
              </div>}
            <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-8956" data-mp="3bgfnb1wua65m">
              {b.productList.map(p => {
            const n = b.qty[p.id] ?? 0;
            return <li key={p.id} className="flex items-center gap-3 py-2.5" data-id="element-8957" data-mp="3l70jkip1iyld">
                    <ProductImage src={p.image} alt="" width={40} className="h-12 w-10 shrink-0 bg-sand p-1" data-id="element-8958" data-mp="3q63mnxlgmuqp" />
                    <div className="min-w-0 flex-1" data-id="element-8959" data-mp="3v5zp3seiee45">
                      {b.planIds.has(p.id) && <p className="text-[11px] text-clay" data-id="element-8960" data-mp="31nj0uv9fn7v">Uit je huidplan</p>}
                      <p className="truncate text-[14px] leading-snug text-ink" data-id="element-8961" data-mp="2zkp3zqzl9xv">{p.name}</p>
                      <p className="text-[12px] text-muted" data-id="element-8962" data-mp="20074116lktzc">
                        {euro(p.price)}
                        {p.usesPerBottle && ` · ${p.usesPerBottle}`}
                      </p>
                    </div>
                    {n === 0 ? <button type="button" onClick={() => b.setProductQty(p.id, 1)} aria-label={`${p.name} toevoegen`} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink ring-1 ring-line transition-shadow duration-150 ease-soft hover:ring-ink/40" data-id="element-8963" data-mp="243n281ybf2e5">
                        <PlusIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8964" data-mp="2nsxdn3r1316g" />
                      </button> : <div className="flex shrink-0 items-center rounded-full bg-ink text-shell" data-id="element-8965" data-mp="xh9kx8e50e3o">
                        <button type="button" onClick={() => b.setProductQty(p.id, n - 1)} aria-label={`Eén minder: ${p.name}`} className="flex h-10 w-9 items-center justify-center" data-id="element-8966" data-mp="tg7jckwqzpyj">
                          <MinusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-8967" data-mp="naq4x25sjnhf" />
                        </button>
                        <span className="min-w-[1rem] text-center text-[13px] tabular-nums" aria-live="polite" data-id="element-8968" data-mp="eiy0p97d1ala">
                          {n}
                        </span>
                        <button type="button" onClick={() => b.setProductQty(p.id, n + 1)} aria-label={`Eén meer: ${p.name}`} className="flex h-10 w-9 items-center justify-center" data-id="element-8969" data-mp="3vtfnnhj069sq">
                          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-8970" data-mp="3ms7pczghl8yn" />
                        </button>
                      </div>}
                  </li>;
          })}
            </ul>
          </div>}
        {b.count > 0 && <p className="mt-3 text-[13px] text-muted" data-id="element-8971" data-mp="1pxuu0vakulyg">
            Gekozen: {b.count} {b.count === 1 ? 'product' : 'producten'}
            {b.tab === 'products' && b.routineId && ' (met je protocol)'}
            {b.tab === 'protocol' && Object.values(b.qty).some(n => n > 0) && ' (met je losse producten)'}
          </p>}
      </section>

      <section aria-labelledby="builder-how" className="mt-9" data-id="element-8972" data-mp="2krngr83qpeuy">
        <h3 id="builder-how" className={stepTitle} data-id="element-8973" data-mp="2kpax8qic2wfs">
          2 · Hoe vaak
        </h3>
        {b.intervals.length > 1 && <div className="mt-3" data-id="element-8974" data-mp="2athe2oa404p1">
            <IntervalChoice intervals={b.intervals} value={b.intervalWeeks} onChange={b.chooseInterval} labelledBy="builder-how" describedBy="builder-how-hint" data-id="element-8975" data-mp="2as67yht6ykur" />
          </div>}
        <p id="builder-how-hint" className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-8976" data-mp="21tb59fpcvsw7">
          {b.empty ? 'Kies eerst wat je bijvult; dan reken ik het uit.' : b.tooSmall ? `Een doos is altijd minstens ${euro(b.minBoxValue)}, zodat verzending gratis blijft. Met alleen dit lukt dat niet: voeg nog een product toe.` : `Elke ${b.intervalWeeks} weken een doos met alleen wat bijna op is. Niet steeds alles: wat langer meegaat, komt minder vaak.`}{' '}
          Je krijgt vooraf een mail; overslaan of pauzeren kan altijd in Mijn omgeving.
        </p>
      </section>

      <section aria-labelledby="builder-preview" className="mt-9" data-id="element-8977" data-mp="1vowii73jpww2">
        <h3 id="builder-preview" className={stepTitle} data-id="element-8978" data-mp="z0gonytel1d5">
          3 · Zo ziet het eruit
        </h3>
        {b.boxes.length > 0 ? <ol className="mt-3 divide-y divide-line border-y border-line" data-id="element-8979" data-mp="13zjw1pwuvjoa">
            <li className="flex items-baseline justify-between gap-3 py-3 text-[14px]" data-id="element-8980" data-mp="180c3qeq2xgig">
              <span className="text-ink" data-id="element-8981" data-mp="1d080pu0d07oi">Nu · je eerste bestelling, alles</span>
              <span className="tabular-nums text-muted" data-id="element-8982" data-mp="2jfsd1bzdz670">{euro(b.firstOrder)}</span>
            </li>
            {b.boxes.map((box, i) => <li key={box.date} className="py-3" data-id="element-8983" data-mp="2odtuxtl2szgw" data-mpi={i}>
                <p className="flex items-baseline justify-between gap-3 text-[14px]" data-id="element-8984" data-mp="hq27p9jl24nm">
                  <span className="text-ink" data-id="element-8985" data-mp="ho7wwo20zsx9">
                    {dayShort(box.date)}
                    {b.giftAt === i + 1 && <span className="ml-2 text-[12px] text-clay" data-id="element-8986" data-mp="rfvhc50609p8">met cadeau</span>}
                  </span>
                  <span className="tabular-nums text-muted" data-id="element-8987" data-mp="weox642xccsk">{euro(box.total)}</span>
                </p>
                <p className="mt-0.5 text-[13px] text-muted" data-id="element-8988" data-mp="3uwu7mwu78uc5">
                  {box.lines.map(l => `${l.qty > 1 ? `${l.qty}× ` : ''}${nameOf(l.productId)}`).join(', ')}
                </p>
              </li>)}
          </ol> : <p className="mt-3 text-[13px] text-muted" data-id="element-8989" data-mp="8nszwrfp8pdx">{b.empty ? 'Nog niets gekozen.' : 'Voeg nog iets toe, dan zie je hier je dozen.'}</p>}
        {b.range && <p className="mt-2 text-[13px] text-muted" data-id="element-8990" data-mp="1k5avv4r1gkpw">
            {b.range.min === b.range.max ? `${euro(b.range.min)} per doos` : `Meestal ${euro(b.range.min)} tot ${euro(b.range.max)} per doos`}, altijd gratis verzending.
          </p>}
      </section>
    </Drawer>;
}